import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Layout from "../components/Layout";
import { CourseSidebar } from "../components/CourseSidebar";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import {
  ChevronRight, ShieldCheck, Menu, CheckCircle2, Circle,
  ArrowLeft, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const COURSE_NAME = "California Property & Casualty";

function slugify(text, i) {
  return `sec-${i}-${(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 40)}`;
}

export default function WorkbookReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [marking, setMarking] = useState(false);

  const loadChapters = useCallback(async () => {
    try { const { data } = await api.get("/workbook"); setChapters(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setDoc(null);
    api.get(`/workbook/${id}`).then((r) => setDoc(r.data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/workbook");
    });
    window.scrollTo(0, 0);
  }, [id, navigate]);

  useEffect(() => { loadChapters(); }, [loadChapters]);

  useEffect(() => {
    const block = (e) => { e.preventDefault(); toast.info("Your place is saved automatically — no need to copy. Everything's here whenever you sign in."); };
    const blockKeys = (e) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c", "u"].includes(k)) { e.preventDefault(); toast.info("No need to save — your workbook is always here in the app when you're signed in."); }
    };
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("contextmenu", block);
    document.addEventListener("dragstart", block);
    document.addEventListener("keydown", blockKeys);
    return () => {
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("dragstart", block);
      document.removeEventListener("keydown", blockKeys);
    };
  }, []);

  // Sanitize + inject heading ids, derive section list
  const { safeHtml, derivedSections } = useMemo(() => {
    if (!doc) return { safeHtml: "", derivedSections: [] };
    const clean = DOMPurify.sanitize(doc.html, { USE_PROFILES: { html: true } });
    const parser = new DOMParser();
    const parsed = parser.parseFromString(`<div id="wb-root">${clean}</div>`, "text/html");
    const root = parsed.getElementById("wb-root");
    const heads = root ? root.querySelectorAll("h2, h3") : [];
    const secs = [];
    heads.forEach((h, i) => {
      const text = h.textContent.trim();
      if (!text) return;
      const sid = slugify(text, i);
      h.setAttribute("id", sid);
      secs.push({ id: sid, text, level: h.tagName === "H3" ? 3 : 2 });
    });
    return { safeHtml: root ? root.innerHTML : clean, derivedSections: secs };
  }, [doc]);

  useEffect(() => { setSections(derivedSections); setActiveSection(derivedSections[0]?.id || null); }, [derivedSections]);

  // Track active section on scroll
  useEffect(() => {
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-90px 0px -70% 0px", threshold: 0 }
    );
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [sections, safeHtml]);

  const chapterIndex = chapters.findIndex((c) => c.id === id);
  const current = chapters[chapterIndex];
  const prev = chapterIndex > 0 ? chapters[chapterIndex - 1] : null;
  const next = chapterIndex >= 0 && chapterIndex < chapters.length - 1 ? chapters[chapterIndex + 1] : null;
  const isDone = !!current?.completed;
  const donePct = chapters.length ? Math.round((chapters.filter((c) => c.completed).length / chapters.length) * 100) : 0;

  const toggleComplete = async () => {
    setMarking(true);
    try {
      const { data } = await api.post(`/workbook/${id}/complete`, { completed: !isDone });
      setChapters((cs) => cs.map((c) => (c.id === id ? { ...c, completed: data.completed } : c)));
      toast.success(data.completed ? "Chapter marked complete" : "Marked as not complete");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMarking(false); }
  };

  const goTo = (ch) => { if (ch) { if (ch.locked) navigate("/upgrade"); else navigate(`/workbook/${ch.id}`); } };

  const sidebar = (onSelect) => (
    <CourseSidebar chapters={chapters} currentId={id} sections={sections} activeSectionId={activeSection} onSelect={onSelect} />
  );

  return (
    <Layout showFooter={false}>
      <div className="max-w-7xl mx-auto w-full flex">
        {/* Desktop left sidebar */}
        <aside className="hidden lg:block w-[288px] shrink-0 border-r border-slate-200 bg-white">
          <div className="sticky top-16 h-[calc(100vh-4rem)]">{sidebar()}</div>
        </aside>

        {/* Main lesson area */}
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-5 no-print-region">
          {/* Breadcrumb + mobile TOC button */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <nav className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0 flex-wrap" data-testid="workbook-breadcrumb">
              <Link to="/library" className="hover:text-[#0A192F]">My Library</Link>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
              <span className="hover:text-[#0A192F]">{COURSE_NAME}</span>
              {current && <><ChevronRight className="h-3.5 w-3.5 text-slate-300" /><span className="text-[#0A192F] font-medium truncate">{current.title}</span></>}
            </nav>
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden shrink-0" data-testid="mobile-toc-btn"><Menu className="h-4 w-4 mr-1.5" /> Course Content</Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[300px]"><SheetTitle className="sr-only">Course Content</SheetTitle>{sidebar(() => setDrawerOpen(false))}</SheetContent>
            </Sheet>
          </div>

          {/* Compact course header */}
          <div className="flex items-center justify-between gap-3 rounded-xl bg-[#0A192F] text-white px-4 sm:px-5 py-3 mb-5">
            <div className="min-w-0">
              <div className="text-[11px] font-mono uppercase tracking-widest text-amber-500">{COURSE_NAME}</div>
              <div className="text-xs text-slate-300 mt-0.5">
                {chapterIndex >= 0 ? `Chapter ${chapterIndex + 1} of ${chapters.length}` : ""} · {donePct}% Complete
              </div>
            </div>
            <span className="hidden sm:flex items-center gap-1 text-[11px] text-slate-300 shrink-0"><ShieldCheck className="h-3.5 w-3.5" /> Your copy</span>
          </div>

          {!doc ? (
            <div className="text-slate-400 py-12">Loading chapter…</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F] leading-tight">{doc.title}</h1>
                <Button
                  size="sm" variant={isDone ? "outline" : "default"} disabled={marking} onClick={toggleComplete}
                  className={isDone ? "shrink-0 text-emerald-600 border-emerald-200" : "shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"}
                  data-testid="workbook-mark-complete"
                >
                  {isDone ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Completed</> : <><Circle className="h-4 w-4 mr-1.5" /> Mark complete</>}
                </Button>
              </div>

              <div className="relative rounded-2xl border border-slate-200 bg-white overflow-hidden no-select" data-testid="workbook-reader">
                <div className="absolute inset-0 grid grid-cols-2 sm:grid-cols-3 gap-y-28 place-items-center overflow-hidden z-0 pointer-events-none" aria-hidden data-testid="workbook-watermark-overlay">
                  {[...Array(30)].map((_, i) => (
                    <span key={i} className="watermark-tile text-[10px] whitespace-nowrap text-slate-900">{doc.watermark}</span>
                  ))}
                </div>
                <div className="relative z-10 p-5 sm:p-10">
                  <div className="workbook-content" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                </div>
              </div>

              {/* Prev / Next chapter nav */}
              <div className="flex items-center justify-between gap-3 mt-6">
                {prev ? (
                  <button onClick={() => goTo(prev)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A192F] group" data-testid="workbook-prev-chapter">
                    <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition" />
                    <span className="text-left"><span className="block text-[11px] text-slate-400">Previous</span>{prev.title}</span>
                  </button>
                ) : <span />}
                {next ? (
                  <button onClick={() => goTo(next)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0A192F] text-right group" data-testid="workbook-next-chapter">
                    <span><span className="block text-[11px] text-slate-400">Next</span>{next.title}</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
                  </button>
                ) : <span />}
              </div>

              <p className="text-center text-xs text-slate-400 mt-6 font-mono no-print">Prepared for {doc.watermark}</p>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
